"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useAuthStore } from "@/stores/auth-store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { PixelText } from "@/components/ui/pixel-text";
import { UserPlus } from "lucide-react";
import { track, identify, ANALYTICS_EVENTS } from "@/lib/analytics";

export default function RegisterPage() {
  const router = useRouter();
  const setUser = useAuthStore((s) => s.setUser);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, password }),
        credentials: "include",
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Registration failed");
        return;
      }

      setUser(data.user);
      if (data.user?.id) identify(String(data.user.id));
      track(ANALYTICS_EVENTS.SIGNUP);
      router.push("/onboarding");
    } catch {
      setError("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card className="p-8">
      <div className="text-center mb-8">
        <PixelText as="h1" glow="pink" size="xl" className="mb-2">
          SURGGIES
        </PixelText>
        <p className="text-[var(--ink-soft)] font-mono text-sm">
          Build your AI empire
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-[var(--ink-soft)] font-mono text-xs mb-1.5 uppercase tracking-wider">
            Name
          </label>
          <Input
            type="text"
            placeholder="Your CEO name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div>
          <label className="block text-[var(--ink-soft)] font-mono text-xs mb-1.5 uppercase tracking-wider">
            Email
          </label>
          <Input
            type="email"
            placeholder="ceo@surggies.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div>
          <label className="block text-[var(--ink-soft)] font-mono text-xs mb-1.5 uppercase tracking-wider">
            Password
          </label>
          <Input
            type="password"
            placeholder="Min 6 characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
          />
        </div>

        {error && (
          <p className="text-[#c54a4a] font-mono text-xs text-center">{error}</p>
        )}

        <Button type="submit" className="w-full" size="lg" disabled={loading}>
          <UserPlus className="w-4 h-4 mr-2" />
          {loading ? "Creating..." : "Create Account"}
        </Button>
      </form>

      <p className="text-center text-[var(--ink-soft)] font-mono text-xs mt-6">
        Already have an account?{" "}
        <Link
          href="/login"
          className="text-[var(--gold-deep)] hover:text-[var(--ink)] underline underline-offset-2"
        >
          Log in
        </Link>
      </p>
    </Card>
  );
}
